{
  // --- Scroll restoration (PDP "Go Back" / browser back → PLP) ---
  const SCROLL_STORAGE_KEY = 'plp:scroll';

  const readStoredPosition = () => {
    try {
      return JSON.parse(sessionStorage.getItem(SCROLL_STORAGE_KEY));
    } catch (err) {
      return null;
    }
  };

  const clearStoredPosition = () => {
    try {
      sessionStorage.removeItem(SCROLL_STORAGE_KEY);
    } catch (err) {
      // sessionStorage unavailable — ignore
    }
  };

  const waitForNextFrame = () => new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(resolve));
  });

  const storedPosition = readStoredPosition();
  const restorePending = !!storedPosition && storedPosition.path === window.location.pathname + window.location.search;

  // Claim scroll restoration early — only when a restore is pending — so the
  // browser doesn't scroll against the not-yet-rebuilt grid.
  if (restorePending && 'scrollRestoration' in history) history.scrollRestoration = 'manual';

  // Release scroll restoration when leaving/reloading so native behaviour
  // applies whenever no restore of ours is pending.
  window.addEventListener('pagehide', () => {
    if ('scrollRestoration' in history) history.scrollRestoration = 'auto';
  });

  // bfcache back-navigation: DOM and scroll are already restored by the browser —
  // clear the stored entry and do nothing else.
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) clearStoredPosition();
  });

  const restoreScrollPosition = async () => {
    if (!restorePending) return;

    const maxWaitMs = 2500;
    const stableFramesRequired = 2;
    const startedAt = Date.now();
    let previousHeight = 0;
    let previousCardTop = null;
    let stableFrames = 0;

    while (Date.now() - startedAt < maxWaitMs) {
      const grid = document.getElementById('product-grid');
      const card = storedPosition.productId
        ? document.querySelector(`#product-grid .snippet-product-grid[data-product-id="${storedPosition.productId}"]`)
        : null;
      const gridHeight = grid ? grid.scrollHeight : 0;
      const cardTop = card ? Math.round(card.getBoundingClientRect().top + window.scrollY) : null;
      const sameHeight = gridHeight > 0 && gridHeight === previousHeight;
      const sameCardTop = cardTop !== null && cardTop === previousCardTop;

      if (card ? sameHeight && sameCardTop : sameHeight) {
        stableFrames++;
      } else {
        stableFrames = 0;
      }

      const layoutReady = card
        ? stableFrames >= stableFramesRequired
        : stableFrames > stableFramesRequired;

      if (layoutReady) {
        if (card) {
          card.scrollIntoView({ block: 'center' });
        } else if (typeof storedPosition.scrollY === 'number') {
          window.scrollTo(0, storedPosition.scrollY);
        }

        clearStoredPosition();
        if ('scrollRestoration' in history) history.scrollRestoration = 'auto';
        return;
      }

      previousHeight = gridHeight;
      previousCardTop = cardTop;
      await waitForNextFrame();
    }

    if (typeof storedPosition.scrollY === 'number') {
      window.scrollTo(0, storedPosition.scrollY);
    }

    clearStoredPosition();
    if ('scrollRestoration' in history) history.scrollRestoration = 'auto';
  };

  // --- Grid view toggle (mobile) ---
  if (window.innerWidth < 1025) {
    document.querySelectorAll('.coll-view span').forEach((span) => {
      span.addEventListener('click', () => {
        const viewClass = span.dataset.view;
        const siblings = span.closest('li').parentElement.querySelectorAll('li span');
        siblings.forEach((s) => s.classList.remove('active'));
        span.classList.add('active');

        const grid = document.querySelector('.collection-grid');
        grid?.classList.remove('view-1', 'view-2');
        grid?.classList.add(viewClass);
      });
    });
  }

  // Init swatches (or defer if product-group-swatches.js hasn't loaded yet)
  if (window.GroupSwatches) {
    window.GroupSwatches.init();
  } else {
    window.addEventListener('groupswatches:ready', () => window.GroupSwatches?.init(), { once: true });
  }

  // --- Helpers shared by load-more & preload ---
  const reinitCards = () => {
    if (typeof quickAddButton === 'function') quickAddButton();
    window.GroupSwatches?.init();
    window.initProductCardVideos?.();
  };

  // --- Load More ---
  const initLoadMore = () => {
    const collectionGrid = document.getElementById('collection-grid');
    const pagination = document.getElementById('collection-pagination');
    if (!collectionGrid || !pagination || pagination.dataset.paginationMode !== 'load') {
      // Numbers mode / single page — keep native behaviour, release scroll restoration
      if (restorePending) {
        clearStoredPosition();
        if ('scrollRestoration' in history) history.scrollRestoration = 'auto';
      }
      return;
    }

    // Guard against double-binding when the script re-executes against the same
    // grid element (e.g. theme dev hot reload) — a second set of delegated
    // listeners would append every Load More page twice
    if (collectionGrid.dataset.loadMoreBound) return;
    collectionGrid.dataset.loadMoreBound = 'true';

    const { sectionId, perPage: rawPerPage, totalProducts: rawTotal, totalPages: rawPages, currentPage: rawPage } = pagination.dataset;
    const perPage = parseInt(rawPerPage, 10) || 28;
    const totalProducts = parseInt(rawTotal, 10) || 0;
    const totalPages = parseInt(rawPages, 10) || 1;
    let currentPage = parseInt(rawPage, 10) || 1;
    let loading = false;

    const loader = () => collectionGrid.querySelector('.collection-loader');
    const showLoader = () => { const el = loader(); if (el) el.style.display = 'flex'; };
    const hideLoader = () => { const el = loader(); if (el) el.style.display = 'none'; };

    const updateUrl = (page) => {
      const url = new URL(window.location.href);
      page > 1 ? url.searchParams.set('page', page) : url.searchParams.delete('page');
      history.replaceState({ page }, '', url.pathname + url.search);
    };

    const fetchPage = async (pageUrl) => {
      const sep = pageUrl.includes('?') ? '&' : '?';
      const res = await fetch(`${pageUrl}${sep}section_id=${sectionId}`);
      const html = await res.text();
      return new DOMParser().parseFromString(html, 'text/html');
    };

    const appendProducts = (doc) => {
      const grid = document.getElementById('product-grid');
      const newGrid = doc.getElementById('product-grid');
      if (!grid || !newGrid) return;
      newGrid.querySelectorAll('.product-item, .featured-section').forEach((item) => {
        // Featured tiles are re-rendered server-side on every page — skip ones already in the grid
        if (item.classList.contains('featured-section') && item.dataset.blockId
          && grid.querySelector(`.featured-section[data-block-id="${item.dataset.blockId}"]`)) return;
        grid.appendChild(item);
      });
    };

    const replaceProducts = (doc) => {
      const grid = document.getElementById('product-grid');
      const newGrid = doc.getElementById('product-grid');
      if (!grid || !newGrid) return;
      grid.innerHTML = newGrid.innerHTML;
    };

    const updatePagination = (doc) => {
      const container = document.getElementById('collection-pagination');
      const fresh = doc.getElementById('collection-pagination');
      if (container) container.innerHTML = fresh ? fresh.innerHTML : '';
    };

    // Event delegation — survives filter.js innerHTML replacement
    collectionGrid.addEventListener('click', async (e) => {
      const btn = e.target.closest('.collection-load-more');
      if (!btn || loading || !btn.dataset.nextUrl) return;
      e.preventDefault();

      loading = true;
      btn.style.display = 'none';
      showLoader();

      try {
        const doc = await fetchPage(btn.dataset.nextUrl);
        appendProducts(doc);
        reinitCards();
        currentPage++;
        updateUrl(currentPage);
        updatePagination(doc);
      } catch (err) {
        console.error('Load more failed:', err);
        btn.style.display = '';
      }

      hideLoader();
      loading = false;
    });

    // Persist grid position when leaving to a PDP — event delegation survives
    // filter.js innerHTML replacement
    collectionGrid.addEventListener('click', (e) => {
      const link = e.target.closest('a[href]');
      if (!link) return;
      const card = link.closest('.snippet-product-grid[data-product-id]');
      if (!card) return;
      try {
        sessionStorage.setItem(SCROLL_STORAGE_KEY, JSON.stringify({
          path: window.location.pathname + window.location.search,
          productId: card.dataset.productId,
          scrollY: window.scrollY,
          ts: Date.now(),
        }));
      } catch (err) {
        // sessionStorage unavailable — ignore
      }
    });

    // Deep-link preload: if URL has ?page=N, the server rendered page N only —
    // rebuild the grid as pages 1..N in order (replace with page 1, then append 2..N)
    const preload = async () => {
      const urlPage = parseInt(new URLSearchParams(window.location.search).get('page'), 10) || 1;
      if (urlPage <= 1) return;

      const cap = Math.min(urlPage, totalPages, 5);
      showLoader();

      const preloadUrl = (page) => {
        const url = new URL(window.location.href);
        url.searchParams.set('page', page);
        return url.pathname + url.search;
      };

      let loadedPage = 0;
      for (let p = 1; p <= cap; p++) {
        try {
          const doc = await fetchPage(preloadUrl(p));
          p === 1 ? replaceProducts(doc) : appendProducts(doc);
          if (p === cap) updatePagination(doc);
          loadedPage = p;
        } catch (err) {
          console.error(`Preload page ${p} failed:`, err);
          break;
        }
      }

      if (loadedPage) currentPage = loadedPage;
      reinitCards();
      hideLoader();
    };

    preload().then(restoreScrollPosition);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initLoadMore);
  } else {
    initLoadMore();
  }
};
